import { gql } from "@apollo/client";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";

export const PRORATION_FRAGMENT = gql`
  fragment ProrationFragment on CommerceLineItemTypeProration {
    originalPrice
    proratedNumberOfMonths
    proratedPrice
  }
`;

export const ORDER_QUERY = gql`
  query commerceOrder($orderId: ID!) {
    commerceOrder(id: $orderId) {
      id
      status
      paymentStatus
      balance
      payable
      totalPaid
      totalRefunded
      lineItemsTotal
      dueDate
      updatedAt
      pdfAvailable
      description
      type
      templateId
      eventRegistration {
        id
        event {
          title
          slug
          startsAt
          activityType
        }
      }
      orderPayments {
        id
        amountCents
        ledgerEntries {
          lineItem {
            id
            priceCents
          }
        }
        payment {
          id
          amountCents
          referenceNumber
          type
          status
          source
          paidAt
          payer {
            id
            name
          }
        }
      }
      person {
        id
        firstName
        lastName
        profilePhoto
        pgaId
        email
        mainProgramType {
          id
          name
        }
        contactAddress {
          id
          address1
          address2
          city
          state
          postalCode
          countryCode
          addressType
        }
      }
      lineItems(includeRemoved: true) {
        id
        name
        priceCents
        notes
        quantity
        displayOrder
        productVariantId
        removedAt
        ledgerEntries {
          createdAt
          type
          amountCents
        }
        proration {
          ...ProrationFragment
        }
        utilization {
          creditForwardedAmount
          lineItemId
          name
          originalPrice
          utilizedEnd
          utilizedNumberOfMonths
          utilizedStart
        }
        refundablePayments {
          paymentId
          amountCents
        }
      }
      notes {
        ...CRMNoteFields
      }
    }
  }

  ${PRORATION_FRAGMENT}
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const CANCEL_ORDER_MUTATION = gql`
  mutation CancelCommerceOrder($input: CommerceCancelCommerceOrderInput!) {
    cancelCommerceOrder(input: $input) {
      order {
        id
        status
      }
      message
      success
    }
  }
`;

export const MARK_ORDER_UNCOLLECTIBLE_MUTATION = gql`
  mutation MarkCommerceOrderUncollectible($input: CommerceMarkCommerceOrderUncollectibleInput!) {
    markCommerceOrderUncollectible(input: $input) {
      order {
        id
        status
        payable
      }
      message
      success
    }
  }
`;

export const OPEN_ORDER_MUTATION = gql`
  mutation OpenCommerceOrder($input: CommerceOpenCommerceOrderInput!) {
    openCommerceOrder(input: $input) {
      order {
        id
        status
        dueDate
      }
      message
      success
    }
  }
`;

export const CHANGE_DUE_DATE_MUTATION = gql`
  mutation ChangeDueDate($input: CommerceChangeOrderDueDateInput!) {
    commerceChangeOrderDueDate(input: $input) {
      order {
        id
        status
        dueDate
      }
      message
      success
    }
  }
`;

export const SET_ORDER_DRAFT_MUTATION = gql`
  mutation SetOrderDraft($input: CommerceSetOrderDraftInput!) {
    commerceSetOrderDraft(input: $input) {
      order {
        id
        status
      }
      message
      success
    }
  }
`;

export const PDF_QUERY = gql`
  query commerceOrder($orderId: ID!) {
    commerceOrder(id: $orderId) {
      id
      pdfUrl
    }
  }
`;

export const CREATE_PAYMENT_REFUND_MUTATION = gql`
  mutation CreatePaymentRefund($input: CommerceCreatePaymentRefundInput!) {
    commerceCreatePaymentRefund(input: $input) {
      refund {
        id
        status
      }
      message
      success
    }
  }
`;

export const GET_PRODUCTS = gql`
  query GetProducts($input: BackofficeCommerceProductInput!) {
    commerceProducts(input: $input) {
      nodes {
        id
        name
        description
      }
    }
  }
`;
export const GET_PRODUCT_VARIANTS = gql`
  query GetProductVariants($productId: String!, $search: String, $templateId: String!, $orderId: String) {
    commerceProductVariants(
      input: { productId: $productId, search: $search, templateId: $templateId, orderId: $orderId }
    ) {
      totalCount
      nodes {
        id
        name
        priceCents
        changeablePrice
        proratedPriceCents
      }
    }
  }
`;

export const COMMERCE_AUDIT_LOG_QUERY = gql`
  query CommerceAuditLog($orderId: ID) {
    commerceAuditLog(orderId: $orderId) {
      id
      timestamp

      eventType
      description
      actor {
        __typename
        ... on BackofficeUser {
          id
          email
          name
        }
        ... on BackofficeAdminUser {
          id
          email
        }
        ... on BackofficePerson {
          id
          displayName
        }
      }
      person {
        id
        displayName
      }
      changeset {
        id
        typeOfData
        oldValue
        newValue
      }
    }
  }
`;
